/**
 * Aarrearkku drop selector.
 * Story 19.8 Phase 2 (Task 2.1); relocated to shared in Story 19.11.
 *
 * Pure function: given the matcher's results and the set of already-owned
 * item ids, decide which item (if any) is awarded for this story.
 *
 * Rules (verbatim from the epic's drop-mechanics section):
 *   - 0 matches → no drop.
 *   - 1+ matches, all already owned → no drop. (Hint side-effects are
 *     produced upstream as console.log only — not the selector's job.)
 *   - 1+ matches with ≥1 unowned → award the *most-common-rarity unowned*
 *     match (Common > Uncommon > Rare). Higher-rarity matches are
 *     withheld in this story to throttle perceived rarity. Tiebreak:
 *     stable, by catalogue order (first occurrence in `matches` wins).
 *
 * @module utils/aarrearkku-drop-selector
 */

import type { MatchResult } from './aarrearkku-matcher';
import { RARITY_ORDER, type TreasureRarity } from '../data/aarrearkku-catalogue';

export type DropDecision =
  | { kind: 'none' }
  | { kind: 'drop'; itemId: string; rarity: TreasureRarity };

const RARITY_TIERS: TreasureRarity[] = (Object.keys(RARITY_ORDER) as TreasureRarity[]).sort(
  (a, b) => RARITY_ORDER[a] - RARITY_ORDER[b]
);

export function selectDrop(matches: MatchResult[], owned: ReadonlySet<string>): DropDecision {
  const unowned = matches.filter((m) => !owned.has(m.itemId));
  if (unowned.length === 0) return { kind: 'none' };

  for (const tier of RARITY_TIERS) {
    const candidate = unowned.find((m) => m.rarity === tier);
    if (candidate) {
      return { kind: 'drop', itemId: candidate.itemId, rarity: candidate.rarity };
    }
  }

  return { kind: 'none' };
}
